
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SponsoredService } from '../services/sponsoredService';
import { useAuth } from './AuthBridge';
import { StorageService } from '../services/storageService';
import { cleanSearchParams } from '../services/authSanitizer';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    dataLayer: any[];
    trackEvent: (action: string, category: string, label?: string, value?: number, customParams?: Record<string, any>) => void;
  }
}

export const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const config = SponsoredService.getConfig();
  const gaScriptInjected = useRef(false);
  const fbScriptInjected = useRef(false);

  const isCategoryAllowed = (cat: 'analytics' | 'marketing') => {
    if (!config.cookieConsent.isEnabled) return true;
    const rawConsent = localStorage.getItem('cna_cookie_consent');
    if (!rawConsent) return false;
    if (rawConsent === 'accepted') return true;
    if (rawConsent === 'declined') return false;
    try {
      const parsed = JSON.parse(rawConsent);
      return Boolean(parsed[cat]);
    } catch {
      return false;
    }
  };

  const checkGaConsent = () => {
    const isProd = process.env.NODE_ENV === 'production';
    const gaEnabled = config.gaEnabled && (isProd || config.gaEnableInDev);
    if (!isCategoryAllowed('analytics')) return false;
    return gaEnabled && !!config.gaTrackingId;
  };

  const checkFbConsent = () => {
    const isProd = process.env.NODE_ENV === 'production';
    const fbEnabled = config.metaPixelEnabled && (isProd || config.metaPixelEnableInDev);
    if (!isCategoryAllowed('marketing')) return false;
    return fbEnabled && !!config.metaPixelId;
  };

  useEffect(() => {
    const runAnalytics = () => {
        // Run Google Analytics
        if (checkGaConsent()) {
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'granted',
                    'ad_storage': 'granted'
                });
            } else if (!gaScriptInjected.current && config.gaTrackingId) {
                const script1 = document.createElement('script');
                script1.async = true;
                script1.src = `https://www.googletagmanager.com/gtag/js?id=${config.gaTrackingId}`;
                document.head.appendChild(script1);

                const script2 = document.createElement('script');
                script2.text = `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('consent', 'default', {
                    'analytics_storage': 'granted',
                    'ad_storage': 'granted'
                  });
                  gtag('config', '${config.gaTrackingId}', {
                    'user_id': '${user?.id || 'anonymous'}',
                    'send_page_view': false
                  });
                `;
                document.head.appendChild(script2);
                gaScriptInjected.current = true;
            }
        } else {
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'denied',
                    'ad_storage': 'denied'
                });
            }
        }

        // Run Meta Pixel
        if (checkFbConsent() && !fbScriptInjected.current) {
            const script = document.createElement('script');
            script.text = `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${config.metaPixelId}');
            `;
            document.head.appendChild(script);
            fbScriptInjected.current = true;
        }
    };

    // Provide a unified trackEvent helper with GA4 parameters support
    window.trackEvent = (action, category, label, value, customParams) => {
        // Track in GA
        if (window.gtag && checkGaConsent()) {
            window.gtag('event', action, {
                event_category: category,
                event_label: label,
                value: value,
                user_id: user?.id || 'anonymous',
                ...customParams
            });
        }

        // Track in Meta Pixel
        if (window.fbq && checkFbConsent()) {
            // Map common actions to standard Meta events or use trackCustom
            const standardEvents = ['ViewContent', 'Search', 'AddToCart', 'InitiateCheckout', 'AddPaymentInfo', 'Purchase', 'Lead', 'CompleteRegistration'];
            if (standardEvents.includes(action)) {
                window.fbq('track', action, { content_category: category, content_name: label, value: value, ...customParams });
            } else {
                window.fbq('trackCustom', action, { category, label, value, ...customParams });
            }
        }

        // Persist to Centralized Database Telemetry (Guest & Student Footprints)
        StorageService.trackUserEvent({
            action,
            category: category || 'interaction',
            label,
            value,
            metadata: customParams
        });
    };

    runAnalytics();

    // Listen for consent updates
    const handleConsentUpdate = (e: any) => {
        if (e.detail === 'accepted') {
            runAnalytics();
        }
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdate);
    return () => window.removeEventListener('cookie-consent-updated', handleConsentUpdate);
  }, [config.gaEnabled, config.gaTrackingId, config.gaEnableInDev, config.metaPixelEnabled, config.metaPixelId, config.metaPixelEnableInDev, config.cookieConsent.isEnabled, user?.id]);

  // Enhanced Page View tracking (capturing exact Hash route)
  useEffect(() => {
    const trackPage = () => {
      if (window.gtag && checkGaConsent()) {
        // Combine pathname, hash routes and query parameters perfectly for precise SPA paths
        const cleanSearch = cleanSearchParams(window.location.search);
        const pagePath = window.location.pathname + window.location.hash + cleanSearch;
        window.gtag('event', 'page_view', {
          page_path: pagePath,
          page_title: document.title,
          user_id: user?.id || 'anonymous'
        });
      }

      if (window.fbq && checkFbConsent()) {
          window.fbq('track', 'PageView');
      }
    };

    // Give doc title 100ms to settle down after routing changes (common in virtual DOM state adjustments)
    const timer = setTimeout(trackPage, 100);
    return () => clearTimeout(timer);
  }, [location, config.gaEnabled, config.metaPixelEnabled, config.cookieConsent.isEnabled, user?.id]);

  // Auto Timing & Route Navigation Tracker for Guest & Registered Sessions
  const initialSearch = cleanSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const lastPathAndTimestamp = useRef({ 
    path: (typeof window !== 'undefined' ? window.location.pathname + window.location.hash + initialSearch : ''), 
    time: Date.now() 
  });
  const isFirstRoute = useRef(true);

  useEffect(() => {
    const now = Date.now();
    const prev = lastPathAndTimestamp.current;
    const cleanSearch = cleanSearchParams(window.location.search);
    const currentPath = window.location.pathname + window.location.hash + cleanSearch;
    
    // Log initial landing immediately on arrival
    if (isFirstRoute.current) {
        isFirstRoute.current = false;
        StorageService.trackUserEvent({
            action: 'page_view',
            category: 'navigation',
            label: currentPath,
            metadata: {
                isInitialLanding: true,
                referrer: document.referrer || 'direct'
            }
        });
    }

    // Compute elapsed seconds for previous route
    const durationSeconds = Math.round((now - prev.time) / 1000);
    
    // Track if user stayed on that route for a valid duration (> 1s)
    if (durationSeconds > 1 && prev.path && prev.path !== currentPath) {
        StorageService.trackUserEvent({
            action: 'page_view_duration',
            category: 'navigation',
            label: prev.path,
            value: durationSeconds
        });
    }

    // Refresh for next route transition
    lastPathAndTimestamp.current = {
        path: currentPath,
        time: now
    };
  }, [location]);

  // Global Interactive Click Interceptor (Buttons & Links)
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Find the closest interactive element (button, custom role/type, or link)
      const actionEl = target.closest('button, [role="button"], [type="button"], a');
      if (!actionEl) return;

      const isLink = actionEl.tagName === 'A' || actionEl.hasAttribute('href');
      const text = (actionEl.textContent || '').trim().replace(/\s+/g, ' ').substring(0, 100);
      const id = actionEl.id || '';
      const classes = actionEl.className || '';
      const href = actionEl.getAttribute('href') || '';

      if (isLink) {
        const isOutbound = href.startsWith('http') && !href.includes(window.location.hostname);
        window.trackEvent(isOutbound ? 'outbound_link_click' : 'internal_link_click', 'Engagement', text || href, undefined, {
          link_url: href,
          link_text: text,
          link_id: id,
          is_outbound: isOutbound
        });
      } else {
        window.trackEvent('button_click', 'Engagement', text || id || 'unlabeled_element', undefined, {
          button_text: text,
          button_id: id,
          button_classes: classes.substring(0, 100)
        });
      }
    };

    window.addEventListener('click', handleGlobalClick, true);
    return () => window.removeEventListener('click', handleGlobalClick, true);
  }, [config.gaEnabled, config.metaPixelEnabled]);

  // Global Unhandled Runtime Exception Tracking
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      try {
        if (window.gtag && checkGaConsent()) {
          const errorMsg = event.error ? event.error.message : event.message;
          const fallbackInfo = `at ${event.filename || 'unknown'}:${event.lineno || 0}:${event.colno || 0}`;
          const stack = event.error && event.error.stack ? event.error.stack.split('\n').slice(0, 3).join(' ') : fallbackInfo;
          
          window.gtag('event', 'exception', {
            description: `${errorMsg} | ${stack}`.substring(0, 250),
            fatal: false,
            event_category: 'Error'
          });
        }
      } catch (e) {}
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      try {
        if (window.gtag && checkGaConsent()) {
          const reason = event.reason;
          const message = reason instanceof Error ? reason.message : String(reason);
          const stack = reason instanceof Error && reason.stack ? reason.stack.split('\n').slice(0, 2).join(' ') : 'No Stack';
          
          window.gtag('event', 'exception', {
            description: `Unhandled Promise Rejection: ${message} | ${stack}`.substring(0, 250),
            fatal: false,
            event_category: 'Error'
          });
        }
      } catch (e) {}
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [config.gaEnabled, config.gaTrackingId]);

  // Page Load Performance Tracker
  useEffect(() => {
    const handlePerformanceMetrics = () => {
      setTimeout(() => {
        try {
          const entries = window.performance.getEntriesByType('navigation');
          const perfData = entries[0] as PerformanceNavigationTiming;
          if (perfData) {
            const dnsTime = Math.max(0, perfData.domainLookupEnd - perfData.domainLookupStart);
            const connectTime = Math.max(0, perfData.connectEnd - perfData.connectStart);
            const responseTime = Math.max(0, perfData.responseEnd - perfData.responseStart);
            const domInteractive = Math.max(0, perfData.domInteractive);
            const loadTime = Math.max(0, perfData.loadEventEnd);

            if (window.gtag && checkGaConsent()) {
              window.gtag('event', 'page_load_performance', {
                dns_lookup_ms: Math.round(dnsTime),
                connection_ms: Math.round(connectTime),
                response_ms: Math.round(responseTime),
                dom_interactive_ms: Math.round(domInteractive),
                total_load_ms: Math.round(loadTime),
                event_category: 'Performance'
              });
            }
          } else if (window.performance.timing) {
            const timing = window.performance.timing;
            const dnsTime = Math.max(0, timing.domainLookupEnd - timing.domainLookupStart);
            const connectTime = Math.max(0, timing.connectEnd - timing.connectStart);
            const responseTime = Math.max(0, timing.responseEnd - timing.responseStart);
            const domInteractive = Math.max(0, timing.domInteractive - timing.navigationStart);
            const loadTime = Math.max(0, timing.loadEventEnd - timing.navigationStart);

            if (window.gtag && checkGaConsent()) {
              window.gtag('event', 'page_load_performance', {
                dns_lookup_ms: Math.round(dnsTime),
                connection_ms: Math.round(connectTime),
                response_ms: Math.round(responseTime),
                dom_interactive_ms: Math.round(domInteractive),
                total_load_ms: Math.round(loadTime),
                event_category: 'Performance'
              });
            }
          }
        } catch (e) {
          console.warn("Performance metric logging failed:", e);
        }
      }, 1000);
    };

    if (document.readyState === 'complete') {
      handlePerformanceMetrics();
    } else {
      window.addEventListener('load', handlePerformanceMetrics);
      return () => window.removeEventListener('load', handlePerformanceMetrics);
    }
  }, [config.gaEnabled, config.gaTrackingId]);

  return null;
};
